// Pre-generation validation for CEFR email sequences.
// Parses CEFR A0/A1-C2 markdown and checks quality gates before allowing email generation.

export interface CheckResult {
  id: string;
  label: string;
  pass: boolean;
  detail: string;
}

export interface ValidationResult {
  level: string;
  languageCode: string;
  a0SectionCount: number;
  a0SpilloverToA2: number;
  checks: CheckResult[];
  allPassed: boolean;
  fixReport: string | null;
}

const BASE_LANG_NAMES: Record<string, string> = {
  en: 'English', ja: 'Japanese', ko: 'Korean', 'zh-TW': 'Chinese',
  'pt-BR': 'Portuguese', 'es-ES': 'Spanish', fr: 'French', ru: 'Russian',
  ar: 'Arabic', th: 'Thai',
};

// ── Transcription pattern config ──
// Loaded from the API at runtime (GET /api/admin/transcription-patterns).
// The database is the source of truth; these types mirror its shape.

export interface TranscriptionPattern {
  title: string;
  transcription: 'none' | 'ipa' | 'romanization';
  levels: string[];
  glo_levels: string[];
  note: string;
  languages: string[];
  vocalization?: Record<string, string>;
  vocalization_levels?: string[];
}

export interface TranscriptionPatternsConfig {
  patterns: Record<string, TranscriptionPattern>;
  translation_languages: Record<string, string>;
}

let _patternsConfig: TranscriptionPatternsConfig | null = null;

export function setTranscriptionPatterns(config: TranscriptionPatternsConfig): void {
  _patternsConfig = config;
  _langToPattern = null;
}

export function getTranscriptionPatterns(): TranscriptionPatternsConfig | null {
  return _patternsConfig;
}

export const patternsReady: Promise<void> =
  typeof fetch === 'function'
    ? fetch(`${import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com'}/api/admin/transcription-patterns`)
        .then(r => r.ok ? r.json() as Promise<TranscriptionPatternsConfig> : Promise.reject(r.status))
        .then(data => { setTranscriptionPatterns(data); })
        .catch(() => {})
    : Promise.resolve();

let _langToPattern: Map<string, TranscriptionPattern> | null = null;
let _langToPatternKey: Map<string, string> | null = null;

function langToPattern(): Map<string, TranscriptionPattern> {
  if (_langToPattern) return _langToPattern;
  _langToPattern = new Map();
  _langToPatternKey = new Map();
  if (_patternsConfig) {
    for (const [key, p] of Object.entries(_patternsConfig.patterns)) {
      for (const lang of p.languages) {
        _langToPattern.set(lang, p);
        _langToPatternKey.set(lang, key);
      }
    }
  }
  return _langToPattern;
}

export function getPatternKey(lang: string): string | null {
  langToPattern();
  return _langToPatternKey?.get(lang) ?? null;
}

export type TranscriptionType = 'none' | 'ipa' | 'romanization';

function isLatinScript(lang: string): boolean {
  const p = langToPattern().get(lang);
  if (!p) return true;
  return p.transcription === 'none' || (p.transcription === 'ipa' && p.glo_levels.length <= 2);
}

export function getTranscriptionInfo(lang: string, level: string): { required: boolean; type: TranscriptionType } {
  const p = langToPattern().get(lang);
  if (!p) return { required: false, type: 'none' };
  const lvl = level.toUpperCase();
  const required = p.levels.indexOf(lvl) !== -1;
  return { required, type: p.transcription };
}

interface Section9Sub {
  index: number;
  title: string;
  body: string;
  vocabCount: number;
  hasNumberTable: boolean;
  hasSituationPatterns: boolean;
  hasMiniDialog: boolean;
  hasVocabTable: boolean;
}

interface Section10Info {
  body: string;
  subsections: string[];
  subsectionNumbers: number[];
  maxSubsection: number;
  sampleSentenceCount: number;
  isJustIndex: boolean;
}

function splitTopSections(md: string): Map<number, string> {
  const sections = new Map<number, string>();
  const lines = md.split('\n');
  let currentNum = -1;
  let currentLines: string[] = [];

  for (const line of lines) {
    const m = line.match(/^## (?:§\s*)?(\d+)\.?\s/);
    if (m) {
      if (currentNum >= 0) sections.set(currentNum, currentLines.join('\n'));
      currentNum = parseInt(m[1]!, 10);
      currentLines = [line];
    } else {
      currentLines.push(line);
    }
  }
  if (currentNum >= 0) sections.set(currentNum, currentLines.join('\n'));
  return sections;
}

function parseSection9(text: string): Section9Sub[] {
  const subs: Section9Sub[] = [];
  const parts = text.split(/(?=^### (?:§\s*)?9\.\d+)/m);

  for (const part of parts) {
    const hm = part.match(/^### (?:§\s*)?9\.(\d+)[.,:;—\s]+(.+)/m);
    if (!hm) continue;
    const index = parseInt(hm[1]!, 10);
    const title = hm[2]!.trim();

    const vocabTableRows = countTableRows(part);
    const hasNumberTable = checkNumberTable(part);
    const hasSituationPatterns = /\*\*situation patterns?:?\*\*/i.test(part) ||
      /#{3,4}\s+situation patterns?/i.test(part) ||
      /situation patterns?/i.test(part) ||
      /場面パターン/i.test(part) ||
      /^\[(?:CAS|POL|FRM|FML|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD)\]\s+\S/m.test(part);
    const hasMiniDialog = /\*\*(?:mini[- ]|review\s+|combined\s+)?dialog/i.test(part) ||
      /#{3,4}\s+(?:mini[- ])?dialog/i.test(part) ||
      /^>\s*\*\*[AB]\*?\*?\s/m.test(part) ||
      /^[AB]\s*\[(?:CAS|POL|FRM|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD)\]\s*:/m.test(part) ||
      /^[AB]\s*:/m.test(part) ||
      /ミニ会話/i.test(part);
    const hasVocabTable = /\*\*(?:core )?vocabulary:?\*\*/i.test(part) ||
      (vocabTableRows >= 3 && /\|\s*\w+\s*\|/.test(part)) ||
      /基本語彙/i.test(part);

    subs.push({
      index,
      title,
      body: part,
      vocabCount: vocabTableRows,
      hasNumberTable,
      hasSituationPatterns,
      hasMiniDialog,
      hasVocabTable,
    });
  }
  return subs;
}

function countTableRows(text: string): number {
  const lines = text.split('\n');
  let count = 0;
  let inTable = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (/^\|[\s-:|]+\|$/.test(trimmed)) {
        inTable = true;
        continue;
      }
      if (inTable || /\|\s*\S/.test(trimmed)) {
        if (!/^\|\s*[-]+/.test(trimmed.replace(/\s/g, ''))) {
          count++;
          inTable = true;
        }
      }
    } else {
      inTable = false;
    }
  }
  // Subtract header rows (one per table)
  const tableStarts = (text.match(/^\|[\s-:|]+\|$/gm) || []).length;
  return Math.max(0, count - tableStarts);
}

function checkNumberTable(text: string): boolean {
  const lines = text.split('\n');
  let hasTable = false;
  for (const line of lines) {
    if (/\|.*\d+.*\|/.test(line) && /\|/.test(line)) {
      hasTable = true;
      break;
    }
  }
  return hasTable && (/\d{1,5}/.test(text));
}

function hasNumberContent(text: string): boolean {
  // Lenient check for 9.2: numbers 1-10 can be in vocab table or listed anywhere
  const hasDigits = /\b[1-9]\b/.test(text) || /\b10\b/.test(text);
  const hasNumberWord = /number|count|digit|numeral/i.test(text);
  return hasDigits || hasNumberWord;
}

function parseSection10(text: string): Section10Info {
  const subsections = (text.match(/^### (?:§\s*)?10\.\d+.*/gm) || []).map(s => s.trim());
  const subsectionNumbers = subsections
    .map(s => { const m = s.match(/^### (?:§\s*)?10\.(\d+)/); return m ? parseInt(m[1]!, 10) : 0; })
    .filter(n => n > 0);
  const maxSubsection = subsectionNumbers.length > 0 ? Math.max(...subsectionNumbers) : 0;

  const lines = text.split('\n');
  let sampleCount = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();
    // Format 1: backtick-wrapped register tag on its own line — `[CAS]`
    if (line.startsWith('`[') && line.endsWith(']`')) {
      const nextLine = lines[i + 1]?.trim() || '';
      if (nextLine && !nextLine.startsWith('#') && !nextLine.startsWith('|') &&
          !nextLine.startsWith('*') && !nextLine.startsWith('>') &&
          !nextLine.startsWith('`') && nextLine.length > 0) {
        sampleCount++;
      }
    }
    // Format 2: bare register tag with sentence on same line — [CAS] sentence...
    else if (/^\[(?:CAS|POL|FRM|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD|FML|HON)\]\s+\S/.test(line)) {
      sampleCount++;
    }
    // Format 3: bare register tag alone on its own line (no backticks) — [CAS]\n sentence on next line
    else if (/^\[(?:CAS|POL|FRM|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD|FML|HON)\]$/.test(line)) {
      const nextLine = lines[i + 1]?.trim() || '';
      if (nextLine && !nextLine.startsWith('#') && !nextLine.startsWith('|') &&
          !nextLine.startsWith('*') && !nextLine.startsWith('>') &&
          !nextLine.startsWith('`') && !nextLine.startsWith('[') && nextLine.length > 0) {
        sampleCount++;
      }
    }
  }

  const hasExamples = sampleCount >= 5;
  const hasExplanations = /\*\*.*:?\*\*/.test(text) && text.length > 500;
  const isJustIndex = !hasExamples && !hasExplanations;

  return {
    body: text,
    subsections,
    subsectionNumbers,
    maxSubsection,
    sampleSentenceCount: sampleCount,
    isJustIndex,
  };
}

function countGloLines(text: string): number {
  return (text.match(/^\[GLO\]/gm) || []).length +
    (text.match(/^>\s*\[GLO\]/gm) || []).length;
}

// Count unlabeled romanization/IPA lines: Latin-script text between a non-Latin target line and [GLO]
function countUnlabeledTranscription(text: string): number {
  const lines = text.split('\n');
  let count = 0;
  for (let i = 1; i < lines.length - 1; i++) {
    const curr = lines[i]!.trim();
    if (!curr || curr.startsWith('#') || curr.startsWith('|') || curr.startsWith('*')
      || curr.startsWith('>') || curr.startsWith('`') || curr.startsWith('[') || curr.startsWith('-')) continue;
    const latinChars = [...curr].filter(c => /[a-zA-Z]/.test(c)).length;
    const allAlpha = [...curr].filter(c => /\p{L}/u.test(c)).length;
    if (allAlpha === 0 || latinChars / allAlpha < 0.8) continue;
    // Prev line should be non-Latin (target language sentence)
    const prev = lines[i - 1]!.trim();
    if (!prev || prev.startsWith('#') || prev.startsWith('|') || prev.startsWith('`')) continue;
    if (prev.startsWith('[') && !prev.startsWith('[GLO]')) continue; // skip if prev is a register tag line itself
    const prevLatin = [...prev].filter(c => /[a-zA-Z]/.test(c)).length;
    const prevAlpha = [...prev].filter(c => /\p{L}/u.test(c)).length;
    if (prevAlpha === 0 || prevLatin / prevAlpha > 0.5) continue; // prev is Latin too, not a target sentence
    count++;
  }
  return count;
}

function countGloWithEmbeddedTarget(text: string): number {
  const lines = text.split('\n');
  let count = 0;
  for (const line of lines) {
    const trimmed = line.replace(/^>\s*/, '').trim();
    if (!trimmed.startsWith('[GLO]')) continue;
    if (/語釈/.test(trimmed)) continue;
    // Flag parenthetical translations like 友達(buddy) but NOT Leipzig-style
    // morpheme annotations like I(younger), you(older-male), 3SG(F)
    const matches = trimmed.match(/\([A-Za-z][A-Za-z\s'\-.,!?/]*\)/g) || [];
    const real = matches.filter(m => {
      const inner = m.slice(1, -1);
      // Allow single-word gloss annotations attached to morphemes (no space before paren)
      const idx = trimmed.indexOf(m);
      const charBefore = idx > 0 ? trimmed[idx - 1] : ' ';
      if (charBefore !== ' ' && !/\s/.test(inner)) return false;
      // Allow common Leipzig-style register/role annotations
      if (/^(younger|older|older-male|older-female|casual|formal|intimate|polite|male|female|humble|neutral|F|M|PL|SG|INCL|EXCL)$/i.test(inner)) return false;
      // Allow ALL-CAPS Leipzig glossing abbreviations: (CL), (PERF), (PART), (COMP), (NEG), (SFP), etc.
      if (/^[A-Z][A-Z0-9.]*$/.test(inner)) return false;
      return true;
    });
    if (real.length > 0) count++;
  }
  return count;
}

function countSpelledOutNumbers(text: string): number {
  const lines = text.split('\n');
  let inNumbers = false;
  let count = 0;
  for (const line of lines) {
    if (/\*\*(?:数字|Numbers):?\*\*/.test(line)) { inNumbers = true; continue; }
    if (line.startsWith('**') && inNumbers) { inNumbers = false; continue; }
    if (line.startsWith('### ') || line.startsWith('## ')) { inNumbers = false; continue; }
    if (!inNumbers || !line.startsWith('|') || line.startsWith('|---') || /^\| English/i.test(line)) continue;
    const parts = line.split('|');
    if (parts.length < 4) continue;
    const baseLangCell = parts[3]!.trim();
    // Detect hiragana-heavy cells (Japanese spelled-out numbers) — 3+ consecutive hiragana
    if (/[ぁ-ん]{3,}/.test(baseLangCell) && !/[0-9０-９万億兆%／]/.test(baseLangCell)) {
      count++;
    }
    // Detect Arabic spelled-out numbers — long Arabic text without digits
    if (/[؀-ۿ]{4,}/.test(baseLangCell) && !/[0-9٠-٩%]/.test(baseLangCell)) {
      count++;
    }
    // Detect Korean spelled-out numbers — hangul without digits
    if (/[가-힯]{3,}/.test(baseLangCell) && !/[0-9%만억조]/.test(baseLangCell)) {
      count++;
    }
  }
  return count;
}

function countTranslationLines(text: string, baseLang: string = 'en'): number {
  const lines = text.split('\n');
  let count = 0;
  const isRegisterTag = (s: string) => /^`?\[(?:CAS|POL|FRM|FML|FOR|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD|HON|ACA|BUR|JUR|PUB|INT|MSA|ROY|SOV|DIAL|ROM|PES|TAG|CF|IPA)\]`?$/.test(s);
  const isInlineTag = (s: string) => /^\[(?:CAS|POL|FRM|FML|FOR|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD|HON|ACA|BUR|JUR|PUB|INT|MSA|ROY|SOV|DIAL)\]\s+\S/.test(s);
  const isRomLine = (s: string) => /^\[ROM\]\s/.test(s);
  const looksLikeTranslation = (s: string) => /^[A-Z*]/.test(s) && /[a-z]/.test(s);
  for (let i = 2; i < lines.length; i++) {
    const curr = lines[i]!.trim().replace(/^>\s*/, '');
    if (!curr || curr.startsWith('[') || curr.startsWith('`') || curr.startsWith('#') || curr.startsWith('|')) continue;
    const prev1 = lines[i - 1]!.trim().replace(/^>\s*/, '');
    const prev2 = lines[i - 2]!.trim().replace(/^>\s*/, '');
    // Format 1: [GLO] line → translation
    if (prev1.startsWith('[GLO]')) { count++; continue; }
    // Format 2: register tag → target sentence → (blank) → translation
    if (prev1 === '' && prev2 && !prev2.startsWith('#') && !prev2.startsWith('|') && !prev2.startsWith('`') && i >= 3) {
      const prev3 = lines[i - 3]?.trim().replace(/^>\s*/, '') || '';
      if (isRegisterTag(prev3)) { count++; continue; }
    }
    // Format 3: register tag → target sentence → translation (no blank)
    if (prev2 && isRegisterTag(prev2) && prev1 && !prev1.startsWith('#') && !prev1.startsWith('|')) {
      if (looksLikeTranslation(curr)) { count++; continue; }
    }
    // Format 4: italic translation *(text)*
    if (/^\*[^*]/.test(curr) && (prev1.startsWith('[GLO]') || isRegisterTag(prev2))) { count++; continue; }
    // Format 5: [TAG] content (inline) → translation on next line
    if (isInlineTag(prev1) && looksLikeTranslation(curr)) { count++; continue; }
    // Format 6: [TAG] → content → [ROM] → translation (ROM between content and translation)
    if (isRomLine(prev1) && i >= 3) {
      const prev3 = lines[i - 3]?.trim().replace(/^>\s*/, '') || '';
      if (isRegisterTag(prev3) && looksLikeTranslation(curr)) { count++; continue; }
    }
    // Format 7: [TAG] content (inline) → [ROM] → translation
    if (isRomLine(prev1) && isInlineTag(prev2) && looksLikeTranslation(curr)) { count++; continue; }
  }
  return count;
}

export function countA0Sections(a0Md: string): number {
  const subsections = a0Md.match(/^### /gm) || [];
  return subsections.length;
}

export function validateCefrDoc(
  level: string,
  languageCode: string,
  cefrMd: string,
  a0Md: string | null,
  baseLang: string = 'en',
): ValidationResult {
  const checks: CheckResult[] = [];
  const sections = splitTopSections(cefrMd);
  const section9Text = sections.get(9) || '';
  const section10Text = sections.get(10) || '';

  const section9Subs = parseSection9(section9Text);
  const section10Info = parseSection10(section10Text);
  const baseLangName = BASE_LANG_NAMES[baseLang] || baseLang;
  const txInfo = getTranscriptionInfo(languageCode, level);
  const transcriptionLabel = txInfo.type === 'ipa' ? 'IPA transcription'
    : txInfo.type === 'romanization' ? 'romanization' : 'none';

  // A0 section counting
  const a0SectionCount = a0Md ? countA0Sections(a0Md) : 0;
  const a0SpilloverToA2 = Math.max(0, a0SectionCount - 25);

  // [GLO] requirement: derived from pattern's glo_levels, fallback to Latin heuristic
  const patternEntry = langToPattern().get(languageCode);
  const requireGlo = patternEntry
    ? patternEntry.glo_levels.indexOf(level.toUpperCase()) !== -1
    : (level === 'A1' || level === 'A2');

  // ── Check 0: Language has a transcription pattern assignment ──
  const patternKey = getPatternKey(languageCode);
  checks.push({
    id: 'transcription_pattern',
    label: 'Language assigned to transcription pattern (A–F)',
    pass: patternKey !== null,
    detail: patternKey !== null
      ? `Pattern ${patternKey}: ${patternEntry?.title ?? 'unknown'}`
      : `${languageCode} is not in any transcription pattern (A–F). Add it on the Linguistics page before generating emails.`,
  });

  // ── Check 1: Number content in 9.2+ (A1 only, evaluates present subsections) ──
  if (level === 'A1') {
    const subsWithNumbers = section9Subs.filter(s => s.index >= 2);
    const missingNumbers = subsWithNumbers.filter(s => {
      if (s.index === 2) return !hasNumberContent(s.body);
      return !s.hasNumberTable;
    });
    checks.push({
      id: 'number_tables',
      label: 'Number content in §9 subsections (9.2+)',
      pass: missingNumbers.length === 0,
      detail: missingNumbers.length === 0
        ? `All ${subsWithNumbers.length} subsections (9.2+) have number content`
        : `Missing number content in: ${missingNumbers.map(s => `9.${s.index}`).join(', ')}`,
    });
  }

  // ── Check 2: §9 subsection count (warn if <25, don't block) ──
  checks.push({
    id: 'section9_count',
    label: '§9 has 25 weekly subsections',
    pass: section9Subs.length >= 25,
    detail: section9Subs.length >= 25
      ? `All 25 subsections present`
      : `${section9Subs.length}/25 subsections found — weeks without §9 content will have no vocabulary section`,
  });

  // ── Check 3: Situation patterns (evaluates present subsections only) ──
  const missingSitPatterns = section9Subs.filter(s => !s.hasSituationPatterns);
  checks.push({
    id: 'situation_patterns',
    label: 'Situation patterns in §9 subsections',
    pass: missingSitPatterns.length === 0,
    detail: missingSitPatterns.length === 0
      ? `All ${section9Subs.length} subsections have situation patterns`
      : `Missing situation patterns in: ${missingSitPatterns.map(s => `9.${s.index}`).join(', ')}`,
  });

  // ── Check 4: Mini-dialogs (allow 2 omissions for review/wrap-up weeks) ──
  const missingDialogs = section9Subs.filter(s => !s.hasMiniDialog);
  const dialogThreshold = Math.max(0, section9Subs.length - 2);
  const dialogsFound = section9Subs.length - missingDialogs.length;
  checks.push({
    id: 'mini_dialogs',
    label: 'Mini-dialogs in §9 subsections',
    pass: dialogsFound >= dialogThreshold,
    detail: missingDialogs.length === 0
      ? `All ${section9Subs.length} subsections have mini-dialogs`
      : missingDialogs.length <= 2
        ? `${dialogsFound}/${section9Subs.length} subsections have mini-dialogs (${missingDialogs.map(s => `9.${s.index}`).join(', ')} omitted — OK for review/wrap-up)`
        : `Missing mini-dialogs in: ${missingDialogs.map(s => `9.${s.index}`).join(', ')} (need all but 2)`,
  });

  // ── Check 5: Vocabulary tables ──
  const missingVocabTables = section9Subs.filter(s => !s.hasVocabTable);
  checks.push({
    id: 'vocab_tables',
    label: 'Vocabulary tables in §9 subsections',
    pass: missingVocabTables.length === 0,
    detail: missingVocabTables.length === 0
      ? `All ${section9Subs.length} subsections have vocabulary tables`
      : `Missing vocabulary tables in: ${missingVocabTables.map(s => `9.${s.index}`).join(', ')}`,
  });

  // ── Check 5: §10 quality (not just an index) ──
  checks.push({
    id: 'section10_quality',
    label: '§10 meets quality standard (not just index)',
    pass: !section10Info.isJustIndex,
    detail: section10Info.isJustIndex
      ? `§10 appears to be just a list of pattern IDs without examples (${section10Info.subsections.length} subsections, ${section10Info.sampleSentenceCount} samples, ${section10Text.length} chars)`
      : `§10 has ${section10Info.subsections.length} subsections with explanations and ${section10Info.sampleSentenceCount} sample sentences`,
  });

  // ── Check 6: §10 has ≥50 sample sentences (more is better) ──
  checks.push({
    id: 'section10_samples',
    label: '§10 has ≥50 sample sentences',
    pass: section10Info.sampleSentenceCount >= 50,
    detail: section10Info.sampleSentenceCount >= 50
      ? `${section10Info.sampleSentenceCount} sample sentences found`
      : `${section10Info.sampleSentenceCount} sample sentences found (need ≥50)`,
  });

  // ── Check 6b: §10 has exactly 10.1–10.8 (no subsections beyond 10.8) ──
  {
    const has1to8 = [1,2,3,4,5,6,7,8].every(n => section10Info.subsectionNumbers.includes(n));
    const beyondCount = section10Info.subsectionNumbers.filter(n => n > 8).length;
    const missing = [1,2,3,4,5,6,7,8].filter(n => !section10Info.subsectionNumbers.includes(n));
    checks.push({
      id: 'section10_structure',
      label: '§10 uses exactly 10.1–10.8 subsections',
      pass: has1to8 && beyondCount === 0,
      detail: !has1to8 && beyondCount > 0
        ? `Missing 10.${missing.join(', 10.')} and has ${beyondCount} subsections beyond 10.8 (max: 10.${section10Info.maxSubsection}). All patterns must be organized into 10.1–10.8 only.`
        : !has1to8
          ? `Missing subsections: 10.${missing.join(', 10.')}. §10 must have all 8 subsections (10.1–10.8).`
          : beyondCount > 0
            ? `Found ${beyondCount} subsections beyond 10.8 (up to 10.${section10Info.maxSubsection}). Reorganize all patterns into 10.1–10.8 only.`
            : `All 8 subsections (10.1–10.8) present, no extras`,
    });
  }

  // ── Check 7: [GLO] glosses ──
  const gloCount = countGloLines(cefrMd);
  if (requireGlo) {
    checks.push({
      id: 'glo_glosses',
      label: '[GLO] interlinear glosses on sample sentences',
      pass: gloCount >= 50,
      detail: `${gloCount} [GLO] lines found${gloCount < 50 ? ' (need ≥50 — at least one per sample sentence)' : ''}`,
    });
  } else {
    checks.push({
      id: 'glo_glosses',
      label: '[GLO] interlinear glosses (not required at this level)',
      pass: true,
      detail: level === 'B1'
        ? `Latin-script language — [GLO] not required at B1`
        : `[GLO] not required at ${level}`,
    });
  }

  // ── Check 8: Base-language translations ──
  const translationCount = countTranslationLines(cefrMd, baseLang);
  checks.push({
    id: 'translations',
    label: `Sample sentences have ${baseLangName} translations`,
    pass: translationCount >= 50,
    detail: `${translationCount} ${baseLangName} translations found${translationCount < 50 ? ' (need ≥50)' : ''}`,
  });

  // ── Check 9: ≥10 vocab items per 9.x (more is better) ──
  const thinVocabSubs = section9Subs.filter(s => s.vocabCount < 10);
  checks.push({
    id: 'vocab_count',
    label: '≥10 vocabulary items in each §9 subsection',
    pass: thinVocabSubs.length === 0,
    detail: thinVocabSubs.length === 0
      ? `All ${section9Subs.length} subsections have ≥10 vocabulary items`
      : `Subsections with <10 vocab: ${thinVocabSubs.map(s => `9.${s.index} (${s.vocabCount})`).join(', ')}`,
  });

  // ── Check 10: [GLO] purity (no embedded target-language parentheticals) ──
  if (requireGlo && gloCount >= 10) {
    const gloContaminated = countGloWithEmbeddedTarget(cefrMd);
    checks.push({
      id: 'glo_purity',
      label: '[GLO] lines free of embedded target-language text',
      pass: gloContaminated <= 5,
      detail: gloContaminated <= 5
        ? `${gloContaminated} [GLO] lines with parenthetical target text (≤5 OK — likely grammar terms)`
        : `${gloContaminated} [GLO] lines contain embedded target-language parentheticals like (word) — these must be removed`,
    });
  }

  // ── Check 11: Numbers table base-lang column uses numerals (A1 only) ──
  if (level === 'A1') {
    const spelledOut = countSpelledOutNumbers(cefrMd);
    checks.push({
      id: 'numbers_format',
      label: 'Numbers table: base-language column uses numerals',
      pass: spelledOut === 0,
      detail: spelledOut === 0
        ? 'All number table cells use compact notation (numerals/kanji)'
        : `${spelledOut} number table cells use spelled-out text instead of numerals (e.g., "じゅうまん" should be "10万", "0.004%" not "れいてんれいれいよんパーセント")`,
    });
  }

  // ── Check 12: Transcription pattern compliance ──
  if (patternEntry) {
    const transInfo = getTranscriptionInfo(languageCode, level);
    const labeledTxLines = (cefrMd.match(/^(?:> )?(?:\*\*)?(?:Romanization|IPA|Transcription|romanization|ipa|transcription)(?:\*\*)?[:\s]/gm) || []).length;
    const unlabeledTxLines = countUnlabeledTranscription(cefrMd);
    const transcriptionLines = labeledTxLines + unlabeledTxLines;
    const registerTaggedLines = (cefrMd.match(/^\[(?:CAS|POL|FRM|FML|FOR|INF|VUL|REG|NEU|LIT|COL|SLG|ARC|HUM|TEC|STD|HON|ACA|BUR|JUR|PUB|INT|MSA|ROY|SOV|DIAL|ROM|PES|TAG|CF|IPA)\]/gm) || []).length;

    if (transInfo.required && transInfo.type !== 'none') {
      const hasEnough = transcriptionLines >= Math.min(registerTaggedLines * 0.3, 20);
      checks.push({
        id: 'transcription_content',
        label: `Transcription lines present (Pattern ${patternKey}: ${transInfo.type} at ${level})`,
        pass: hasEnough,
        detail: hasEnough
          ? `${transcriptionLines} transcription lines found for ${registerTaggedLines} examples`
          : `Only ${transcriptionLines} transcription lines for ${registerTaggedLines} examples — Pattern ${patternKey} requires ${transInfo.type} at ${level}`,
      });
    } else if (!transInfo.required && transInfo.type === 'none') {
      checks.push({
        id: 'transcription_content',
        label: `No unnecessary transcription (Pattern ${patternKey}: Latin script)`,
        pass: true,
        detail: `Pattern ${patternKey}: no transcription needed`,
      });
    }
  }

  const NON_BLOCKING = new Set(['section9_count']);
  const allPassed = checks.every(c => c.pass || NON_BLOCKING.has(c.id));

  let fixReport: string | null = null;
  if (!allPassed) {
    fixReport = generateFixReport(level, languageCode, checks, section9Subs, section10Info, a0SectionCount, baseLangName, transcriptionLabel);
  }

  return {
    level,
    languageCode,
    a0SectionCount,
    a0SpilloverToA2,
    checks,
    allPassed,
    fixReport,
  };
}

function generateFixReport(
  level: string,
  languageCode: string,
  checks: CheckResult[],
  section9Subs: Section9Sub[],
  section10Info: Section10Info,
  a0Sections: number,
  baseLangName: string,
  transcriptionLabel: string,
): string {
  const failed = checks.filter(c => !c.pass);
  const lines: string[] = [
    `# CEFR ${level} ${languageCode} — Pre-Generation Fix Report`,
    '',
    `**Generated:** ${new Date().toISOString()}`,
    `**A0 phonics sections:** ${a0Sections}`,
    `**§9 subsections found:** ${section9Subs.length}/25`,
    `**§10 sample sentences:** ${section10Info.sampleSentenceCount}`,
    '',
    `## Failed Checks (${failed.length}/${checks.length})`,
    '',
  ];

  for (const check of failed) {
    lines.push(`### ${check.label}`);
    lines.push(`**Status:** FAIL`);
    lines.push(`**Detail:** ${check.detail}`);
    lines.push('');

    // Add specific fix instructions per check
    switch (check.id) {
      case 'transcription_pattern':
        lines.push(`**Fix:** Go to **Linguistics → Transcription Patterns** and add \`${languageCode}\` to the correct pattern (A–F). The pattern determines whether the CEFR document uses IPA, romanization, or no transcription, and at which levels.`);
        lines.push('- **A** — No transcription (transparent Latin orthography)');
        lines.push('- **B** — IPA at A1/A2 (opaque Latin spelling)');
        lines.push('- **C** — Romanization at A1/A2 (Cyrillic, Greek)');
        lines.push('- **D** — Romanization at A1–B1 (Georgian, Armenian, etc.)');
        lines.push('- **E** — Romanization at A1–B2 (Indic, SE Asian scripts)');
        lines.push('- **F** — Romanization at all levels (CJK, Arabic-script, Hebrew)');
        break;
      case 'number_tables':
        lines.push('**Fix:** Add progressive number content to each §9 subsection from 9.2 onward. 9.2 may list 1-10 in the vocabulary table; 9.3-9.25 need dedicated number subsections:');
        lines.push('- 9.2: 1–10 · 9.3: 11–20 · 9.4: 21–30 · 9.5: 31–40 · 9.6: 41–50');
        lines.push('- 9.7: 51–60 · 9.8: 61–70 · 9.9: 71–80 · 9.10: 81–90 · 9.11: 91–100');
        lines.push('- 9.12: 100–1,000 + compound hundreds (134, 356, 578, 790, 902)');
        lines.push('- 9.13: 1,000–10,000 + year notation (1,945 vs year 1945)');
        lines.push('- 9.14: 10K–100K · 9.15: 100K–1M · 9.16: 1M–10M · 9.17: 10M–100M');
        lines.push('- 9.18: 100M–1B · 9.19: 1B–10B · 9.20: 10B–100B · 9.21: 100B–1T');
        lines.push('- 9.22: Ordinals 1st–10th · 9.23: Percentages/decimals (1%, 0.2%, 0.03%, 0.004%, 1.05%)');
        lines.push('- 9.24: Fractions (1/2, 1/3, 2/3, 3/4, 5/8) · 9.25: Negatives (-1 to -10)');
        lines.push('Format: markdown table with native script, romanization, value columns.');
        break;
      case 'section9_count':
        lines.push(`**Fix:** §9 currently has ${section9Subs.length} subsections but needs 25 (one per email week). Add subsections 9.${section9Subs.length + 1} through 9.25 following the same structure as existing subsections.`);
        lines.push('Note: generation will still work with fewer subsections — weeks without §9 content will simply omit the vocabulary section.');
        break;
      case 'situation_patterns':
        lines.push('**Fix:** Each §9 subsection needs a **Situation patterns:** heading with 2–4 situational usage examples showing register-tagged phrases in context.');
        break;
      case 'mini_dialogs':
        lines.push(`**Fix:** At least 23 of 25 §9 subsections need an A/B conversation (review/wrap-up weeks may omit). Each turn should include: register tag, target language, ${transcriptionLabel}, [GLO] gloss (if required), ${baseLangName} translation.`);
        break;
      case 'vocab_tables':
        lines.push(`**Fix:** Each §9 subsection needs a **Core vocabulary:** table (markdown) with ≥10 rows. Columns: target language | ${transcriptionLabel} | ${baseLangName}.`);
        break;
      case 'section10_quality':
        lines.push('**Fix:** §10 must contain full grammar explanations, not just pattern ID lists. Each subsection (10.1–10.8) needs:');
        lines.push('- A prose explanation of the grammar point');
        lines.push(`- Multiple register-tagged sample sentences with ${transcriptionLabel}, [GLO], and ${baseLangName} translation`);
        break;
      case 'section10_samples':
        lines.push(`**Fix:** §10 currently has ${section10Info.sampleSentenceCount} sample sentences. Add more examples across all subsections to reach ≥50 total. Each sample needs: register tag, target language, ${transcriptionLabel}, [GLO] gloss, ${baseLangName} translation.`);
        break;
      case 'section10_structure':
        lines.push('**Fix:** §10 must have exactly 8 subsections organized by part of speech:');
        lines.push('- 10.1 Verb Agreement (Conjugation)');
        lines.push('- 10.2 Tense');
        lines.push('- 10.3 Aspect');
        lines.push('- 10.4 Mood (Modality)');
        lines.push('- 10.5 Noun Roles (Prepositions & Articles)');
        lines.push('- 10.6 Adjectives & Comparison');
        lines.push('- 10.7 Adverbs');
        lines.push('- 10.8 Sentence Structure & Connectors');
        lines.push('');
        lines.push('Slight naming variation is OK for different language families, but the 8-category structure is mandatory.');
        lines.push('Do NOT create 10.9, 10.10, etc. — fold all grammar patterns into the appropriate 10.1–10.8 section.');
        lines.push(`Each pattern needs: (1) intro explanation, (2) ≥2 sample sentences with register tag + ${transcriptionLabel} + [GLO] + ${baseLangName} translation.`);
        break;
      case 'glo_glosses':
        lines.push('**Fix:** Every sample sentence in the document must have a [GLO] interlinear gloss line. Format:');
        lines.push('```');
        lines.push('`[POL]`');
        lines.push('target language sentence');
        lines.push(`*${transcriptionLabel}*`);
        lines.push('[GLO] word-by-word.gloss with.grammatical.tags');
        lines.push(`${baseLangName} translation`);
        lines.push('```');
        break;
      case 'translations':
        lines.push(`**Fix:** Every sample sentence must have a ${baseLangName} translation on the line after [GLO]. Scan the document for any sample sentences that are missing their ${baseLangName} translation line.`);
        break;
      case 'vocab_count': {
        const thin = section9Subs.filter(s => s.vocabCount < 10);
        lines.push(`**Fix:** The following subsections have fewer than 10 vocabulary items in their table. Add more rows:`);
        for (const s of thin) {
          lines.push(`- 9.${s.index} "${s.title}": ${s.vocabCount} items (need ≥10)`);
        }
        break;
      }
      case 'glo_purity':
        lines.push('**Fix:** [GLO] lines must contain ONLY base-language glosses — no parenthetical target-language translations.');
        lines.push('Remove all `(English word)` patterns from [GLO] lines. Exception: mixed grammar terms like `(to不定詞)` or `(do強調)` are OK.');
        lines.push('```');
        lines.push('WRONG:  [GLO] 友達(buddy)・だ(is)');
        lines.push('RIGHT:  [GLO] 友達・だ');
        lines.push('```');
        break;
      case 'numbers_format':
        lines.push('**Fix:** The base-language column in number tables must use compact numerals/kanji, not spelled-out text.');
        lines.push('The learner already knows their own numbers — spelled-out text is visual noise.');
        lines.push('```');
        lines.push('WRONG:  | one million | /.../ | ひゃくまん |');
        lines.push('RIGHT:  | one million | /.../ | 100万 |');
        lines.push('WRONG:  | negative five | /.../ | マイナスご |');
        lines.push('RIGHT:  | negative five | /.../ | -5 |');
        lines.push('```');
        break;
      case 'transcription_content':
        lines.push(`**Fix:** This language's transcription pattern requires ${transcriptionLabel} at ${level}. Every register-tagged sample sentence must include a transcription line between the native script and the [GLO]/translation lines.`);
        lines.push('CRITICAL: transcription lines must contain ACTUAL phonetic rendering, NOT morpheme glosses.');
        lines.push('```');
        lines.push('WRONG (gloss on transcription line):');
        lines.push('  *I-NOM book-ACC read-PST*');
        lines.push('RIGHT (actual romanization):');
        lines.push('  *na-neun chaeg-eul ilg-eoss-da*');
        lines.push('```');
        break;
    }
    lines.push('');
  }

  lines.push('---');
  lines.push('');
  lines.push('Use this report as a prompt in a CEFR-drafting session to fix the document before attempting email generation again.');

  return lines.join('\n');
}
